import { useState, useEffect } from 'react';
import { X, AlertCircle, Info, AlertTriangle, AlertOctagon } from 'lucide-react';
import { api } from '../services/api';
import { socket } from '../services/socket';

interface Broadcast {
  id: string;
  title: string;
  message: string;
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  expiresAt: string | null;
  creator: {
    id: string;
    name: string;
    email: string;
  };
}

export default function BroadcastBanner() {
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [dismissedIds, setDismissedIds] = useState<string[]>([]);

  useEffect(() => {
    loadActiveBroadcasts();

    // Load dismissed IDs from localStorage
    const dismissed = localStorage.getItem('dismissedBroadcasts');
    if (dismissed) {
      try {
        setDismissedIds(JSON.parse(dismissed));
      } catch (e) {
        console.error('Error parsing dismissed broadcasts:', e);
      }
    }

    // Listen for WebSocket events
    socket.on('new-broadcast', (broadcast: Broadcast) => {
      console.log('New broadcast received:', broadcast);
      if (broadcast.isActive) {
        setBroadcasts((prev) => [broadcast, ...prev]);
      }
    });

    socket.on('broadcast-updated', (broadcast: Broadcast) => {
      console.log('Broadcast updated:', broadcast);
      setBroadcasts((prev) => {
        const filtered = prev.filter((b) => b.id !== broadcast.id);
        if (broadcast.isActive) {
          return [broadcast, ...filtered];
        }
        return filtered;
      });
    });

    socket.on('broadcast-deleted', (data: { id: string }) => {
      console.log('Broadcast deleted:', data.id);
      setBroadcasts((prev) => prev.filter((b) => b.id !== data.id));
    });

    return () => {
      socket.off('new-broadcast');
      socket.off('broadcast-updated');
      socket.off('broadcast-deleted');
    };
  }, []);

  const loadActiveBroadcasts = async () => {
    try {
      const response = await api.getBroadcasts({ active: true });
      setBroadcasts(response.broadcasts || []);
    } catch (error) {
      console.error('Error loading active broadcasts:', error);
    }
  };

  const handleDismiss = (id: string) => {
    const newDismissed = [...dismissedIds, id];
    setDismissedIds(newDismissed);
    localStorage.setItem('dismissedBroadcasts', JSON.stringify(newDismissed));
  };

  const getPriorityConfig = (priority: Broadcast['priority']) => {
    switch (priority) {
      case 'URGENT':
        return {
          bgColor: 'bg-red-100 dark:bg-red-900/30',
          borderColor: 'border-red-300 dark:border-red-700',
          textColor: 'text-red-900 dark:text-red-100',
          icon: AlertOctagon,
          iconColor: 'text-red-600 dark:text-red-400',
        };
      case 'HIGH':
        return {
          bgColor: 'bg-orange-100 dark:bg-orange-900/30',
          borderColor: 'border-orange-300 dark:border-orange-700',
          textColor: 'text-orange-900 dark:text-orange-100',
          icon: AlertTriangle,
          iconColor: 'text-orange-600 dark:text-orange-400',
        };
      case 'NORMAL':
        return {
          bgColor: 'bg-blue-100 dark:bg-blue-900/30',
          borderColor: 'border-blue-300 dark:border-blue-700',
          textColor: 'text-blue-900 dark:text-blue-100',
          icon: Info,
          iconColor: 'text-blue-600 dark:text-blue-400',
        };
      case 'LOW':
        return {
          bgColor: 'bg-gray-100 dark:bg-gray-700',
          borderColor: 'border-gray-300 dark:border-gray-600',
          textColor: 'text-gray-900 dark:text-gray-100',
          icon: AlertCircle,
          iconColor: 'text-gray-600 dark:text-gray-400',
        };
      default:
        return {
          bgColor: 'bg-gray-100 dark:bg-gray-700',
          borderColor: 'border-gray-300 dark:border-gray-600',
          textColor: 'text-gray-900 dark:text-gray-100',
          icon: Info,
          iconColor: 'text-gray-600 dark:text-gray-400',
        };
    }
  };

  // Filter out dismissed broadcasts
  const visibleBroadcasts = broadcasts.filter((b) => !dismissedIds.includes(b.id));

  if (visibleBroadcasts.length === 0) return null;

  return (
    <div className="space-y-2">
      {visibleBroadcasts.map((broadcast) => {
        const config = getPriorityConfig(broadcast.priority);
        const Icon = config.icon;

        return (
          <div
            key={broadcast.id}
            className={`${config.bgColor} ${config.borderColor} border rounded-lg p-3 flex items-start gap-3`}
          >
            <Icon className={`w-5 h-5 ${config.iconColor} flex-shrink-0 mt-0.5`} />
            <div className="flex-1 min-w-0">
              <h4 className={`font-semibold ${config.textColor} text-sm mb-1`}>
                {broadcast.title}
              </h4>
              <p className={`${config.textColor} text-sm whitespace-pre-wrap`}>
                {broadcast.message}
              </p>
              {broadcast.expiresAt && (
                <p className={`text-xs ${config.textColor} opacity-75 mt-2`}>
                  Válido até: {new Date(broadcast.expiresAt).toLocaleString('pt-BR')}
                </p>
              )}
            </div>
            <button
              onClick={() => handleDismiss(broadcast.id)}
              className={`${config.iconColor} hover:opacity-70 transition flex-shrink-0`}
              title="Dispensar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
